import { ReadMore } from '@components/read-more/ReadMore';
import { useDetailedPricing } from '@hooks/use-detailed-pricing';
import { useTrackStorefrontImpression } from '@hooks/useTrackStorefrontImpression';
import { useTrackStorefrontInteraction } from '@hooks/useTrackStorefrontInteraction';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { TextBody, TextTitle } from '@tkww/orion-web-ds-text';
import React, { type FC, useCallback, useState } from 'react';
import Styles from './styles.scss';

export interface Props {
	description?: string | null;
	vendorName: string;
}

interface HeaderProps {
	isDetailedPricing: boolean;
	isMobile: boolean;
}

const Header = ({ isDetailedPricing, isMobile }: HeaderProps) => {
	const aboutTrackingRef = useTrackStorefrontImpression('About vendor');

	if (isMobile && isDetailedPricing) {
		return (
			<TextTitle size="sm" className={Styles.aboutHead} ref={aboutTrackingRef}>
				About
			</TextTitle>
		);
	}

	return (
		<TextTitle size="sm" className={Styles.aboutHead} ref={aboutTrackingRef}>
			About this vendor
		</TextTitle>
	);
};

interface DefaultMessageProps {
	vendorName: string;
}
const DefaultMessage = ({ vendorName }: DefaultMessageProps) => (
	<div className={Styles.defaultAbout}>
		We don‘t know {vendorName}‘s story by heart. Message them to get to know
		more about their business.
	</div>
);

interface AboutContentProps {
	description: string;
}

const defaultClampCharacters = (description: string, isMobile: boolean) => {
	if (isMobile) {
		return description.length >= 300;
	}

	return description.length >= 550;
};

const AboutContent = ({ description }: AboutContentProps) => {
	const [expanded, setExpanded] = useState(false);
	const isMobile = useAppSelector((state) => selectIsMobile(state));
	const track = useTrackStorefrontInteraction('About Vendor');

	const trackSeeMore = useCallback(() => {
		track(expanded ? 'Hide' : 'Read more');
	}, [track, expanded]);

	return (
		<div className={Styles.aboutContent}>
			<ReadMore
				clamp
				clampDefaultShowLink={defaultClampCharacters(description, isMobile)}
				clampLines={isMobile ? 8 : 6}
				classes={{
					container: Styles.readMoreContainer,
				}}
				linkColor="primary"
				expanded={expanded}
				setExpanded={setExpanded}
				onToggle={trackSeeMore}
			>
				<TextBody size="md" className={Styles.desc}>
					{description}
				</TextBody>
			</ReadMore>
		</div>
	);
};

const VendorDescription: FC<Props> = ({ description = null, vendorName }) => {
	const isMobile = useAppSelector(selectIsMobile);
	const isInDetailedPricing = useDetailedPricing();

	return (
		<div className={Styles.aboutContainer}>
			<Header isDetailedPricing={isInDetailedPricing} isMobile={isMobile} />
			<div className={Styles.contentWrapper}>
				<div className={Styles.aboutContentContainer}>
					{description ? (
						<AboutContent description={description} />
					) : (
						<DefaultMessage vendorName={vendorName} />
					)}
				</div>
			</div>
		</div>
	);
};

export default VendorDescription;
